import React, { useState } from "react";

import { CListGroupItem, CListGroup } from "@coreui/react";
import fileDownload from "js-file-download";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Progress,
  Button
} from "reactstrap";

import URL from "../../../config/api";
import axios from "../../../config/axios";
import { arrayToCSV, elementToPDF, arrayToXLSX } from "../../../utils/export";
import Charts from "../../Charts";
import { ContentCard } from "../../ContentCard";
import Loading from "../../Loading";

// Conteúdo da aba "Habilidade" das estatísticas: gráfico de acertos, lista de
// habilidades com o percentual da turma e, ao expandir, as atividades sugeridas
// para cada habilidade.
//
// Vive separado do index.jsx para ser reaproveitado por telas que já têm o
// próprio filtro e buscam os dados por conta (ex.: o relatório da avaliação
// diagnóstica) — aqui não há DataContext nem barra de filtros, só o `data`
// devolvido por statistics/evaluation_corrects?type=ability.
export default function AcertosPorHabilidadePainel({
  data,
  exactPercentage,
  allocateQuery,
  chartRowHeight
}) {
  // Por padrão (a aba do modal) o percentual de acertos aparece como inteiro
  // arredondado. Com exactPercentage ele é TRUNCADO em uma casa decimal, sem
  // arredondar: 76,88% mostra 76,8% e não 76,9%. É o que o relatório da
  // avaliação diagnóstica usa.
  const percentageDecimals = exactPercentage ? 1 : 0;

  const [activities, setActivities] = useState({});
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [viewedActivity, setViewedActivity] = useState(null);

  const token = useSelector(state => state.user.token);

  const fetchActivitiesByAbility = async abilityId => {
    const obj = { ability_id: abilityId, page: 1, pageSize: 5 };

    const activitiesByAbility = await axios.post(
      `${URL.api}activity/activity/listByAbility`,
      obj,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setActivities(prev => ({
      ...prev,
      [abilityId]: activitiesByAbility.data.data
    }));
  };

  const formatAbility = (ability, ellipsis = true) => {
    const text = ability.description
      ? `${ability.code && `(${ability.code}) `}${ability.description}`
      : "(Sem habilidade)";

    if (!ellipsis || text.length < 150) return text;

    return `${text.substring(0, 150)}...`;
  };

  const getCorrects = abilityId => {
    if (!data.applications || !data.abilities) {
      return "";
    }

    const correct = data.applications.find(
      item => item.ability_id === abilityId && item.correct
    )?.count;

    return correct || 0;
  };

  const getPercentage = abilityId => {
    if (!data.applications || !data.abilities) {
      return 0;
    }

    const correct =
      data.applications.find(
        item => item.ability_id === abilityId && item.correct
      )?.count || 0;
    const notCorrect =
      data.applications.find(
        item => item.ability_id === abilityId && !item.correct
      )?.count || 0;
    const total = correct + notCorrect;

    if (total === 0) {
      return 0;
    }

    const result = (correct / total) * 100;

    return exactPercentage ? result : Math.round(result);
  };

  /* O valor como a tela mostra: truncado na casa decimal exibida, para nunca
     arredondar para cima. Math.trunc em vez de toFixed justamente por isso —
     (76.88).toFixed(1) daria "76.9". */
  const getDisplayPercentage = abilityId => {
    const percentage = getPercentage(abilityId);
    const factor = 10 ** percentageDecimals;

    return Math.trunc(percentage * factor) / factor;
  };

  /* Formatado em pt-BR: separador decimal vírgula, com a quantidade de casas
     sempre fixa (76,8% / 76,0%). */
  const formatPercentage = abilityId =>
    getDisplayPercentage(abilityId).toLocaleString("pt-BR", {
      minimumFractionDigits: percentageDecimals,
      maximumFractionDigits: percentageDecimals
    });

  /* "Disponibilizar" abre a alocação da atividade numa aba nova, levando os
     queryParams de `allocateQuery` — o recorte dos filtros e a origem — para os
     filtros de lá já abrirem selecionados. Vai pela URL de propósito: aba nova é
     outro documento, então nada de state em memória atravessa, e a tela de
     alocação não guarda esse recorte em localStorage para ser lido do outro
     lado. */
  const openAllocate = activityId => {
    const params = new URLSearchParams();

    Object.entries(allocateQuery ?? {}).forEach(([param, value]) => {
      if (value) params.set(param, value);
    });

    const query = params.toString();

    window
      .open(
        `/activity/allocate/${activityId}${query ? `?${query}` : ""}`,
        "_blank"
      )
      ?.focus();
  };

  const getCSV = data => {
    const header = ["habilidade", "total_acertos", "porcentagem_acertos"];

    const arr = [header];

    data.forEach(ability => {
      arr.push([
        ability.description,
        getCorrects(ability.ability_id),
        getPercentage(ability.ability_id)
      ]);
    });

    return arr;
  };

  const openItem = async itemAux => {
    switch (itemAux.type_id) {
      case 12: // Lista de exercícios
        window.open(`/activity/show/${itemAux.activity_id}`, "_blank").focus();
        break;

      case 14: // Literarte
        break;

      case 27: // Link
      case 16:
        window.open(itemAux.content_path, "_blank").focus();
        break;

      case 17: // Arquivo
        {
          const downloadPath = itemAux.link
            ? itemAux.link.substring(itemAux.link.indexOf("uploads/") + 8)
            : `${itemAux.obj_id === 1 ? "activity" : "repo"}/${itemAux.name}`;

          axios
            .get(`${URL.api}download/${downloadPath}`, {
              headers: { authorization: `Bearer ${token}` },
              responseType: "blob"
            })
            .then(resp => {
              fileDownload(resp.data, itemAux.name);
            });
        }
        break;

      default:
        axios
          .get(`${URL.api}content/los/singleView/${itemAux.obj_id}`, {
            headers: { authorization: `Bearer ${token}` }
          })
          .then(
            resp => {
              const contentData = resp.data.data;
              window
                .open(
                  `/content/view/${contentData.id}?title=${contentData.name}`,
                  "_blank"
                )
                .focus();
            },
            () => {
              toastr.error(
                "Falha",
                "Não foi possível abrir o link. Tente novamente!"
              );
            }
          );
        break;
    }
  };

  return (
    <>
      {/* Modal da atividade sugerida (botão "Visualizar"). O xl deixa a modal
          larga — e faz o breakpoint xl das colunas valer, então os cards cabem
          três por linha. O `scrollable` faz o corpo dela rolar quando a lista de
          conteúdos passa da altura da tela, em vez de ser cortada, e o pb-4
          evita que o último card encoste na borda de baixo. */}
      <Modal
        isOpen={viewedActivity}
        toggle={null}
        className={null}
        onOpened={() => {
          axios
            .get(
              `${URL.api}activity/activity/singleView/${viewedActivity.id}`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            )
            .then(response => {
              const contentList = response.data.data.items.map(item => ({
                type: item.type_id,
                list: {
                  id: item.obj_id,
                  name: item.name
                },
                link: item.link,
                path: item.content_path
              }));

              const data = { ...response.data.data, contentList };
              setViewedActivity(data);
            });
        }}
        size="xl"
        scrollable
      >
        <ModalHeader toggle={() => setViewedActivity(null)}>
          {viewedActivity?.name}
        </ModalHeader>
        <ModalBody className="pb-4">
          <div>
            <strong>Série: </strong>
            {viewedActivity?.serie_name}
          </div>
          <div>
            <strong>Disciplinas: </strong>
            {viewedActivity?.discipline_name}
          </div>
          <Row>
            {viewedActivity?.items?.map(item => (
              <Col xs="12" md="6" xl="4" key={item.id}>
                <ContentCard.Root height="auto">
                  <ContentCard.Banner
                    fallbackColor={item.type_color}
                    fallbackIcon={item.type_icon}
                    actions={
                      item.type_id !== 10 &&
                      item.type_id !== 14 &&
                      item.type_id !== 15 && [
                        {
                          title: "Abrir conteúdo",
                          icon: "external-link",
                          action: () => openItem(item)
                        }
                      ]
                    }
                    height="108px"
                  />
                  <ContentCard.Main>
                    <ContentCard.Header
                      title={item.name}
                      description={item.type_name}
                    />
                  </ContentCard.Main>
                </ContentCard.Root>
              </Col>
            ))}
          </Row>
        </ModalBody>
      </Modal>
      {data.abilities?.length > 0 ? (
        <div className="w-100">
          <div className="d-flex w-100 justify-content-end mt-3 mb-2 no-print">
            <Button
              id="printBtn"
              color="link"
              className="text-muted p-0 mr-2"
              size="sm"
              onClick={() =>
                arrayToXLSX(
                  getCSV(data.abilities),
                  "Acertos por Habilidade.xslx"
                )
              }
            >
              <i className="fa fa-file-pdf-o  mr-1"></i> Exportar para XLSX
            </Button>
            <Button
              id="printBtn"
              color="link"
              className="text-muted p-0"
              size="sm"
              onClick={() =>
                arrayToCSV(getCSV(data.abilities), "Acertos por Habilidade.csv")
              }
            >
              <i className="fa fa-file-pdf-o mr-1"></i> Exportar para CSV
            </Button>
            <span className="mx-2 text-muted">|</span>
            <Button
              id="printBtn"
              color="link"
              className="text-muted p-0"
              size="sm"
              onClick={() =>
                elementToPDF("toPrint", "Acertos por Habilidade.pdf")
              }
            >
              <i className="fa fa-file-pdf-o  mr-1"></i> Exportar para PDF
            </Button>
          </div>
          <div className="w-100" id="toPrint">
            <div className="management-1statistics layoutTabelaComparacao flex-column w-100 mb-4">
              <div className="row">
                <div className="chart w-100">
                  <Charts
                    legendY="Habilidade"
                    legendX="Acertos %"
                    type="horizontalbar"
                    title=""
                    rowHeight={chartRowHeight}
                    data={{
                      labels: data.abilities.map(
                        ability => ability.code || "(Sem habilidade)"
                      ),
                      datasets: [
                        {
                          data: [
                            ...(data.abilities.map(ability =>
                              getPercentage(ability.ability_id)
                            ) || []),
                            100
                          ],
                          backgroundColor: [
                            "rgba(255, 99, 132, 0.2)",
                            "rgba(54, 162, 235, 0.2)",
                            "rgba(255, 206, 86, 0.2)",
                            "rgba(75, 192, 192, 0.2)",
                            "rgba(153, 102, 255, 0.2)",
                            "rgba(255, 159, 64, 0.2)",
                            "rgba(255, 0, 41, 0.2)",
                            "rgba(55, 126, 184, 0.2)",
                            "rgba(102, 166, 30, 0.2)",
                            "rgba(152, 78, 163, 0.2)",
                            "rgba(0, 210, 213, 0.2)",
                            "rgba(255, 127, 0, 0.2)",
                            "rgba(254, 224, 182, 0.2)",
                            "rgba(127, 128, 205, 0.2)",
                            "rgba(179, 233, 0, 0.2)",
                            "rgba(196, 46, 96, 0.2)"
                          ],
                          borderColor: [
                            "rgba(255, 99, 132, 1)",
                            "rgba(54, 162, 235, 1)",
                            "rgba(255, 206, 86, 1)",
                            "rgba(75, 192, 192, 1)",
                            "rgba(153, 102, 255, 1)",
                            "rgba(255, 159, 64, 1)",
                            "rgba(255, 0, 41, 1)",
                            "rgba(55, 126, 184, 1)",
                            "rgba(102, 166, 30, 1)",
                            "rgba(152, 78, 163, 1)",
                            "rgba(0, 210, 213, 1)",
                            "rgba(255, 127, 0, 1)",
                            "rgba(254, 224, 182, 1)",
                            "rgba(127, 128, 205, 1)",
                            "rgba(179, 233, 0, 1)",
                            "rgba(196, 46, 96, 1)"
                          ],
                          borderWidth: 1
                        }
                      ]
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="w-100" style={{ marginBottom: "40px" }}>
              <CListGroup className="h-100 w-100 d-flex flex-column align-items-center justify-content-center border-0">
                {data.abilities.map(ability => (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center bg-light w-100 p-0 mb-2"
                    style={{ borderRadius: "5px" }}
                    key={ability.ability_id}
                  >
                    <CListGroupItem
                      action
                      style={{
                        borderRadius: "5px",
                        cursor: "pointer",
                        height: "72px"
                      }}
                      onClick={() => {
                        setExpandedMenu(
                          expandedMenu === ability.ability_id
                            ? null
                            : ability.ability_id
                        );

                        if (!activities[ability.ability_id]) {
                          fetchActivitiesByAbility(ability.ability_id);
                        }
                      }}
                      className="cursor w-100 border d-flex align-items-center bg-white justify-content-between"
                    >
                      <p
                        className="user-select-none m-0 p-0 w-100"
                        title={ability.description}
                      >
                        {formatAbility(ability)}
                      </p>
                      <div
                        className="mx-4 bg-secondary h-100"
                        style={{
                          width: "1px",
                          minWidth: "1px"
                        }}
                      />
                      <div
                        className="d-flex align-items-center justify-content-between w-25"
                        style={{ minWidth: "25%" }}
                      >
                        <div className="d-flex flex-column align-itens-center justify-content-start w-100">
                          <div className="clearfix">
                            <div className="float-left">
                              {formatPercentage(ability.ability_id)}%
                            </div>
                          </div>
                          <Progress
                            className="progress-xs w-100"
                            color={
                              getPercentage(ability.ability_id) > 50
                                ? "success"
                                : getPercentage(ability.ability_id) <= 50 &&
                                  getPercentage(ability.ability_id) >= 30
                                ? "warning"
                                : "danger"
                            }
                            value={getDisplayPercentage(ability.ability_id)}
                          />
                        </div>

                        <i
                          className="ml-4 fa fa-chevron-right"
                          style={{
                            fontSize: "10px",
                            transform:
                              expandedMenu === ability.ability_id
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                            transition: "0.3s"
                          }}
                        ></i>
                      </div>
                    </CListGroupItem>
                    {expandedMenu === ability.ability_id && (
                      <Row className="h-100 w-100 pb-3">
                        {!activities[ability.ability_id] ? (
                          <Loading />
                        ) : activities[ability.ability_id].length > 0 ? (
                          <>
                            {activities[ability.ability_id].map(
                              (activity, index) => (
                                <Col xs="12" md="6" xl="4" key={index}>
                                  <ContentCard.Root height="auto">
                                    <ContentCard.Main>
                                      <ContentCard.Header
                                        title={activity.name}
                                      />
                                      <ContentCard.Body>
                                        <ContentCard.Item>
                                          Disciplina:
                                          {` ${activity.discipline_name}`}
                                        </ContentCard.Item>
                                      </ContentCard.Body>
                                    </ContentCard.Main>
                                    <ContentCard.Footer>
                                      <Button
                                        size="sm"
                                        color="light"
                                        onClick={() =>
                                          setViewedActivity(activity)
                                        }
                                      >
                                        Visualizar
                                      </Button>
                                      <Button
                                        size="sm"
                                        color="primary"
                                        className="ml-2"
                                        onClick={() =>
                                          openAllocate(activity.id)
                                        }
                                      >
                                        Disponibilizar
                                      </Button>
                                    </ContentCard.Footer>
                                  </ContentCard.Root>
                                </Col>
                              )
                            )}
                          </>
                        ) : (
                          <div className="d-flex align-items-center justify-content-center w-100 h-100 text-muted mt-3">
                            Nenhuma atividade encontrada
                          </div>
                        )}
                      </Row>
                    )}
                  </div>
                ))}
              </CListGroup>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center justify-content-center w-100 h-100 text-muted mt-3">
          Nenhuma habilidade encontrada
        </div>
      )}
    </>
  );
}
